import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RaButtonComponent } from '@shared/components';
import { RaEmptyComponent, RaSquareIconComponent } from 'ra-ui';
import { QuoteItemEntity } from 'src/app/domain/entities/quotes/quote-item.entity';
import { QuoteTaxeEntity } from 'src/app/domain/entities/quotes/quote-taxe.entity';
import { QuoteItemTypeEnum } from 'src/app/domain/enums/quote-item-type.enum';
import { RaCurrencyPipe } from 'src/app/shared/pipes/ra-currency.pipe';
import { UnitOfMeasurementFormatPipe } from 'src/app/shared/pipes/unit-of-measurement-format.pipe';
import { EditService } from '../../services/edit-service';
import { QuoteEditService } from '../../services/quote-edit.service';
import {
    ModalQuoteCatalogComponent,
    QuoteCatalogMode,
} from './modals/modal-quote-catalog/modal-quote-catalog.component';
import { ModalQuoteDiscountFormComponent } from './modals/modal-quote-discount-form/modal-quote-discount-form.component';
import { ModalQuoteItemFormComponent } from './modals/modal-quote-item-form/modal-quote-item-form.component';
import { ModalQuoteTaxeFormComponent } from './modals/modal-quote-taxe-form/modal-quote-taxe-form.component';

/**
 * Painel de edição do orçamento (quote) — tabelas de itens/taxas, desconto,
 * assinatura e totais. Espelha o fluxo do app Flutter, adaptado para web
 * (tabelas inline + modais). Lê/escreve via [QuoteEditService].
 */
@Component({
    selector: 'quote-editor',
    templateUrl: 'quote-editor.component.html',
    styleUrl: 'quote-editor.component.scss',
    imports: [
        CommonModule,
        TranslatePipe,
        RaButtonComponent,
        RaSquareIconComponent,
        RaEmptyComponent,
        RaCurrencyPipe,
        UnitOfMeasurementFormatPipe,
        ModalQuoteCatalogComponent,
        ModalQuoteItemFormComponent,
        ModalQuoteTaxeFormComponent,
        ModalQuoteDiscountFormComponent,
    ],
    providers: [QuoteEditService],
})
export class QuoteEditorComponent {
    public service = inject(QuoteEditService);
    public editService = inject(EditService);

    // * [Estado dos modais]
    public showItemModal = signal(false);
    public itemToEdit = signal<QuoteItemEntity | null>(null);

    public showTaxeModal = signal(false);
    public taxeToEdit = signal<QuoteTaxeEntity | null>(null);

    public showDiscountModal = signal(false);

    /** `null` = catálogo fechado; o valor diz QUAL catálogo está aberto. */
    public catalogMode = signal<QuoteCatalogMode | null>(null);

    // * [Loading global compartilhado — alimenta o estado dos modais]
    public loading = computed(() => this.editService.loading());

    // * [Helpers]
    public lineSubtotal(item: QuoteItemEntity): number {
        return (item.unitPrice ?? 0) * (item.quantity ?? 0);
    }

    public formatPercent(value: number | null | undefined): string {
        const amount = typeof value === 'number' && Number.isFinite(value) ? value : 0;
        const formatted = new Intl.NumberFormat(this.service.locale() || 'pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
        return `${formatted}%`;
    }

    // * [Items]
    public openNewItem(): void {
        if (this.service.isBlocked()) return;
        this.itemToEdit.set(null);
        this.showItemModal.set(true);
    }

    public openEditItem(item: QuoteItemEntity): void {
        if (this.service.isBlocked()) return;
        this.itemToEdit.set(item);
        this.showItemModal.set(true);
    }

    public closeItemModal(): void {
        this.showItemModal.set(false);
        this.itemToEdit.set(null);
    }

    public onItemSaveNew(event: { item: QuoteItemEntity; saveInCatalog: boolean }): void {
        this.service.addItem(event.item, event.saveInCatalog, () => this.closeItemModal());
    }

    public onItemSaveEdit(event: {
        name: string;
        quantity: number;
        unitPrice: number;
        type: QuoteItemTypeEnum;
    }): void {
        const itemId = this.itemToEdit()?._id;
        if (!itemId) return;
        this.service.updateItem(
            itemId,
            event.name,
            event.quantity,
            event.unitPrice,
            event.type,
            () => this.closeItemModal()
        );
    }

    // * [Catálogo da empresa]
    public openCatalog(mode: QuoteCatalogMode): void {
        if (this.service.isBlocked()) return;
        this.catalogMode.set(mode);
    }

    public closeCatalog(): void {
        this.catalogMode.set(null);
    }

    /**
     * Adiciona sem fechar o catálogo: montar um orçamento costuma pegar vários itens
     * seguidos, e reabrir a cada um custaria dois cliques por item.
     */
    public onCatalogAddItem(item: QuoteItemEntity): void {
        this.service.addItem(item, false);
    }

    public onCatalogAddTaxe(taxe: QuoteTaxeEntity): void {
        this.service.addTaxe(taxe, false);
    }

    public removeItem(item: QuoteItemEntity): void {
        if (this.service.isBlocked() || !item._id) return;
        this.service.removeItem(item._id);
    }

    // * [Taxes]
    public openNewTaxe(): void {
        if (this.service.isBlocked()) return;
        this.taxeToEdit.set(null);
        this.showTaxeModal.set(true);
    }

    public openEditTaxe(taxe: QuoteTaxeEntity): void {
        if (this.service.isBlocked()) return;
        this.taxeToEdit.set(taxe);
        this.showTaxeModal.set(true);
    }

    public closeTaxeModal(): void {
        this.showTaxeModal.set(false);
        this.taxeToEdit.set(null);
    }

    public onTaxeSaveNew(event: { taxe: QuoteTaxeEntity; saveInCatalog: boolean }): void {
        this.service.addTaxe(event.taxe, event.saveInCatalog, () => this.closeTaxeModal());
    }

    public onTaxeSaveEdit(event: { name: string; price: number }): void {
        const taxeId = this.taxeToEdit()?._id;
        if (!taxeId) return;
        this.service.updateTaxe(taxeId, event.name, event.price, () => this.closeTaxeModal());
    }

    public removeTaxe(taxe: QuoteTaxeEntity): void {
        if (this.service.isBlocked() || !taxe._id) return;
        this.service.removeTaxe(taxe._id);
    }

    // * [Discount]
    public openDiscountModal(): void {
        if (this.service.isBlocked()) return;
        this.showDiscountModal.set(true);
    }

    public closeDiscountModal(): void {
        this.showDiscountModal.set(false);
    }

    public onDiscountSave(event: { mode: 'percentage' | 'fixed'; value: number }): void {
        this.service.addDiscount(event.mode, event.value, () => this.closeDiscountModal());
    }

    public removeDiscount(): void {
        if (this.service.isBlocked()) return;
        this.service.removeDiscount();
    }

    // * [Signature]
    public toggleSignature(): void {
        if (this.service.isBlocked()) return;
        if (this.service.isPrivate()) {
            this.service.setPublic();
        } else {
            this.service.setPrivate();
        }
    }
}
